import { useState, useCallback } from "react";

interface FeedbackState {
  showSuccess: boolean;
  showError: boolean;
  successMessage: string;
  error: any;
}

interface UseFeedbackOptions {
  successMessage?: string;
  successDuration?: number;
  autoResetError?: boolean;
  autoDismiss?: boolean;
  dismissDelay?: number;
}

export const useFeedback = (options: UseFeedbackOptions = {}) => {
  const {
    successMessage = "Operation completed successfully!",
    successDuration = 3000,
    autoResetError = false,
    autoDismiss = false,
    dismissDelay = 5000,
  } = options;

  const [state, setState] = useState<FeedbackState>({
    showSuccess: false,
    showError: false,
    successMessage,
    error: null,
  });

  const showSuccess = useCallback(
    (message?: string) => {
      setState((prev) => ({
        ...prev,
        showSuccess: true,
        showError: false,
        successMessage: message || successMessage,
        error: null,
      }));

      // Auto-hide success message after duration
      setTimeout(() => {
        setState((prev) => ({ ...prev, showSuccess: false }));
      }, successDuration);
    },
    [successMessage, successDuration]
  );

  const showError = useCallback(
    (error: any) => {
      setState((prev) => ({
        ...prev,
        showError: true,
        showSuccess: false,
        error,
      }));

      // Auto-hide error if enabled
      if (autoResetError) {
        setTimeout(() => {
          setState((prev) => ({ ...prev, showError: false, error: null }));
        }, dismissDelay);
      }
    },
    [autoResetError, dismissDelay]
  );

  const dismissSuccess = useCallback(() => {
    setState((prev) => ({ ...prev, showSuccess: false }));
  }, []);

  const dismissError = useCallback(() => {
    setState((prev) => ({ ...prev, showError: false, error: null }));
  }, []);

  const reset = useCallback(() => {
    setState({
      showSuccess: false,
      showError: false,
      successMessage,
      error: null,
    });
  }, [successMessage]);

  // Mutation handlers for React Query
  const createMutationHandlers = useCallback(
    (
      options: {
        onSuccess?: () => void;
        onError?: (error: any) => void;
        successMessage?: string;
      } = {}
    ) => ({
      onSuccess: () => {
        showSuccess(options.successMessage);
        options.onSuccess?.();
      },
      onError: (error: any) => {
        showError(error);
        options.onError?.(error);
      },
    }),
    [showSuccess, showError]
  );

  return {
    // State
    ...state,

    // Actions
    showSuccess,
    showError,
    dismissSuccess,
    dismissError,
    reset,

    // Helpers
    createMutationHandlers,

    // Computed values
    hasSuccess: state.showSuccess,
    hasError: state.showError && state.error,
  };
};

// Backward compatibility alias
export const useFormFeedback = useFeedback;
